import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Pulse Media intro splash — first visit per browser session.
 * Uses the animated PNG-sequence WebP logo, rendered small and centered
 * over a full-screen black bloom. Auto-dismisses after the loop completes.
 */
const SPLASH_DURATION_MS = 2600; // ~ one full webp loop

export default function IntroSplash({ onDone, force = false }) {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    if (force) return true;
    try { return !sessionStorage.getItem("pulse_intro_played"); } catch { return true; }
  });
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!visible) { onDone?.(); return; }
    const t = setTimeout(() => finish(), SPLASH_DURATION_MS);
    return () => clearTimeout(t);
  }, [visible]);

  const finish = () => {
    try { sessionStorage.setItem("pulse_intro_played", "1"); } catch {}
    setFading(true);
    setTimeout(() => { setVisible(false); onDone?.(); }, 500);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      {!fading && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.5 } }}
          data-testid="intro-splash"
        >
          {/* small centered animated logo (PNG sequence -> webp) */}
          <motion.img
            src="/assets/logo-anim.webp"
            alt="Pulse Media"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 select-none pointer-events-none"
            style={{
              width: "min(160px, 22vw)",
              height: "auto",
              objectFit: "contain",
            }}
            data-testid="intro-logo"
          />

          <button
            onClick={finish}
            className="glass-btn ghost text-xs"
            style={{ position: "absolute", bottom: 40, right: 40, zIndex: 20 }}
            data-testid="intro-skip"
          >
            Skip intro →
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
